// Pure search / sort helpers for the Daily-View "Dropped / Unfilled" panel.
//
// The panel lists one row per order × SKU that the model could not fill. The
// list is small (tens of rows) so a plain tokenised substring match is the
// right tool: every typed token must appear in the row's folded
// "order + SKU" key. Folding goes through `normSearchKey` (the same
// punctuation normaliser the Sankey search bar uses) and then drops the
// remaining spaces, so separator style never matters: "safecure" and
// "safe cure" both find "Safe-Cure 1200", "au00101" finds "(AU00101)".

import type { UnfilledRow } from './mppHelpers';
import { normSearchKey } from './searchIndex';

export type UnfilledSortKey = 'lbs' | 'order' | 'sku';
export type SortDir = 'asc' | 'desc';

export interface UnfilledSort {
  key: UnfilledSortKey;
  dir: SortDir;
}

/** Default ordering: biggest shortfall first (matches unfilledOrderRows). */
export const DEFAULT_UNFILLED_SORT: UnfilledSort = { key: 'lbs', dir: 'desc' };

/** Fold text for matching: lower-case, punctuation and spaces removed. */
function compact(s: string): string {
  return normSearchKey(s).replace(/ /g, '');
}

/** The folded text a row is matched against. Exported for tests. */
export function unfilledRowKey(row: UnfilledRow): string {
  return compact(`${row.order} ${row.sku}`);
}

/**
 * Keep the rows whose folded key contains EVERY token of the query. An empty
 * or whitespace-only query returns the input unchanged (same reference) so
 * callers can cheaply detect "no filter applied".
 */
export function filterUnfilledRows(
  rows: readonly UnfilledRow[],
  query: string,
): readonly UnfilledRow[] {
  const tokens = normSearchKey(query).split(' ').filter(Boolean).map(compact);
  if (tokens.length === 0) return rows;
  return rows.filter((row) => {
    const key = unfilledRowKey(row);
    return tokens.every((t) => key.includes(t));
  });
}

/**
 * Sort a copy of the rows. Text keys compare case-insensitively with a
 * numeric-aware collation so "AU00099" sorts before "AU00100"; ties fall back
 * to the shortfall so the order is total and stable across renders.
 */
export function sortUnfilledRows(
  rows: readonly UnfilledRow[],
  sort: UnfilledSort,
): UnfilledRow[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  return [...rows].sort((a, b) => {
    let cmp: number;
    if (sort.key === 'lbs') {
      cmp = a.lbs - b.lbs;
    } else {
      cmp = collator.compare(a[sort.key], b[sort.key]);
    }
    if (cmp === 0 && sort.key !== 'lbs') cmp = b.lbs - a.lbs;
    return cmp * sign;
  });
}

/**
 * Flip direction when the same column is clicked again, otherwise switch to
 * the new column with its natural default (lbs: biggest first; text: A→Z).
 */
export function nextUnfilledSort(
  current: UnfilledSort,
  clicked: UnfilledSortKey,
): UnfilledSort {
  if (current.key === clicked) {
    return { key: clicked, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key: clicked, dir: clicked === 'lbs' ? 'desc' : 'asc' };
}

export interface UnfilledTotals {
  orders: number;
  lines: number;
  lbs: number;
}

/** Distinct orders, order×SKU lines and total unfilled lbs in a row set. */
export function unfilledTotals(rows: readonly UnfilledRow[]): UnfilledTotals {
  const orders = new Set<string>();
  let lbs = 0;
  for (const r of rows) {
    orders.add(r.order);
    lbs += r.lbs;
  }
  return { orders: orders.size, lines: rows.length, lbs };
}
